<template>
  <div class="setting-details details">
    <div class="close" v-on:click="close()">
      <b-icon icon="close" size="is-large" />
    </div>
    <b-field :label="setting.key|toWords" :class="fieldClasses">
      <template v-if="widget === 'key_value_list'">
        <string-key-value
          v-for="(item, itemIndex) in value"
          :key="['key-val', setting.key, itemIndex].join('-')"
          :option="item"
          :index="itemIndex"
          :total="value.length"
          :isNew="isNew(item)"
          :integer="castValueToInt"
          :editKeys="mayEditKeys"
          :options="selectOptions"
        />
        <b-button
          v-if="mayEditKeys"
          @click="addOption"
          class="float-right"
          icon-left="plus"
          rounded
      >Add new option</b-button>
      </template>
      <template v-if="widget === 'textarea'">
        <b-input
          maxlength="500"
          rows="15"
          type="textarea"
          v-model="widgetValue"
          :has-counter="false"
        ></b-input>
      </template>
      <template v-if="widget === 'text'">
        <b-input maxlength="128" required="true" type="text" v-model="value"></b-input>
      </template>
      <template v-if="widget === 'select'">
        <b-select placeholder="Choose one" v-model="value" required>
          <option
            v-for="opt in options"
            :value="opt.key"
            :key="['setting-opt',opt.key].join('-')"
          >{{opt.label}}</option>
        </b-select>
      </template>
      <template v-if="widget === 'checkbox'">
        <b-switch v-model="value">{{ value|yesNo }}</b-switch>
      </template>
      <template v-if="widget === 'number'">
        <b-numberinput v-model="value" controls-position="compact" controls-rounded></b-numberinput>
      </template>
      <template v-if="widget === 'number-input'">
        <b-input v-model="value" type="number" :step="setting.step"></b-input>
      </template>
      <template v-if="widget === 'date'">
        <b-datepicker :first-day-of-week="1" placeholder="DD/MM/YYYY" v-model="dateValue"></b-datepicker>
        <b-timepicker
          placeholder="Enter a time as HH:mm"
          icon="clock"
          :increment-minutes="5"
          v-model="dateValue"
          inline
          editable
        ></b-timepicker>
      </template>
      <div v-if="hasDefaultOptions" class="default-options">
        <ul>
          <li
            v-for="(optVal, optIndex) in defaultOptions"
            :key="['def', optVal, optIndex].join('_')"
          >{{optVal.key}}</li>
        </ul>
      </div>
    </b-field>
    <b-field label="Sort order">
      <b-numberinput :max="100" controls-position="compact" controls-rounded v-model="weight"></b-numberinput>
    </b-field>
    <b-button type="is-success" @click="save" class="save" rounded>Save</b-button>
    <ul v-if="errorMessages.length > 0" class="error">
      <li v-for="(msg, mi) in errorMessages" :key="['error-msg', mi].join('-')">{{msg}}</li>
    </ul>
    <p v-if="saveMessage.length > 2" class="message">{{saveMessage}}</p>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";
import StringKeyValue from "./StringKeyValue";
import filters from "../mixins/filters";
import { saveSetting, bulkSaveSnippets } from "../api/methods";
import { extractNestedOptions, toKeyString } from "../api/helpers";
import { matchKeyValueOptions } from "../api/mappers";

export default {
  name: "SettingDetail",
  components: {
    StringKeyValue
  },
  props: {
    setting: {
      type: Object,
      default() {
        return {
          _id: "",
          key: "",
          type: "",
          value: "",
          weight: 0,
          displayType: "scalar",
          widget: "text",
          transform: "none",
          step: 0,
          createdAt: "",
          modifiedAt: ""
        };
      }
    }
  },
  mixins: [filters],
  mounted() {
    if (this.setting instanceof Object) {
      if (this.setting.value) {
        this.value = this.setting.value;

        switch (this.setting.transform) {
          case "option_key_values":
            this.widgetValue = this.value.join("\n");
            setTimeout(this.mapOptions, 500);
            break;
          case "key_value_list":
            this.defaultOptions = this.value.map(o => o.key);
            break;
          default:
            this.widgetValue = this.value;
            break;
        }
        if (this.setting.type === "datetime") {
          this.dateValue = new Date(this.value);
        }
      }
      if (this.setting.weight) {
        this.weight = this.setting.weight;
      }
    }
  },
  data() {
    return {
      value: null,
      widgetValue: null,
      dateValue: new Date(),
      weight: 0,
      saveMessage: "",
      errorMessages: [],
      defaultOptions: [],
      defaultListId: ""
    };
  },
  computed: {
    ...mapGetters(["settings", "options", "snippets"]),
    widget() {
      return this.setting.widget;
    },
    options() {
      let opts = [];
      if (this.setting.options) {
        if (this.setting.options instanceof Array) {
          opts = this.setting.options;
        }
      }
      return opts;
    },
    hasDefaultOptions() {
      return this.defaultOptions.length > 0;
    },
    snippetKey() {
      switch (this.setting.key) {
        case "enabled_art_media":
          return "work__medium";
        case "enabled_graduate_statuses":
          return "submission__graduate_status";
        default:
          return "-";
      }
    },
    fieldClasses() {
      return [
        this.setting.type.replace(/_+/g, "-"),
        [this.setting.widget, "group"].join("-")
      ];
    },
    mayEditKeys() {
      switch (this.setting.key) {
        case "min_image_size":
          return false;
        default:
          return true;
      }
    },
    castValueToInt() {
      switch (this.setting.key) {
        case "min_image_size":
          return true;
        default:
          return false;
      }
    },
    selectOptions() {
      return matchKeyValueOptions(this.setting.key);
    }
  },
  methods: {
    ...mapMutations(["setSettings", "setSnippets"]),
    close() {
      this.$parent.close();
    },
    save() {
      const { widget, transform } = this.setting;
      let hasSaved = false;
      let valid = true;
      this.errorMessages = [];
      if (widget === "textarea" && transform === "option_key_values") {
        const valPairs = this.buildValuePairs();
        if (this.defaultOptions.length > 0) {
          this.saveRelatedLabels(valPairs);
          this.saveEnabledKeys(valPairs);
          hasSaved = true;
        } else {
          this.value = valPairs.map(p => p[0]);
        }
      }

      if (widget === "key_value_list") {
        if (this.value instanceof Array) {
          if (this.value.length > 0) {
            this.value = this.value.filter(item => {
              let itemValid = false;
              if (item instanceof Object) {
                if (item.value) {
                  itemValid = item.value.trim().length > 1;
                }
              }
              return itemValid;
            });
            this.value.forEach((row, itemIndex) => {
              const otherKeys = this.value
                .filter((r, ri) => ri !== itemIndex)
                .map(r => r.key);
              if (otherKeys.indexOf(row.key) >= 0) {
                valid = false;
                if (this.errorMessages.length < 1) {
                  this.errorMessages.push(
                    "Duplicate key: " + row.key + " for " + row.value
                  );
                }
              }
            });
          }
        }
      }

      if (widget === "date") {
        this.value = this.dateValue.toISOString();
      }

      if (widget === "number-input") {
        this.value = parseFloat(this.value);
      }

      if (widget === "number") {
        this.value = parseInt(this.value, 10);
      }
      if (widget === "checkbox") {
        switch (typeof this.value) {
          case "number":
            this.value = this.value > 0;
            break;
          case "string":
            this.value = parseInt(this.value, 10) > 0;
            break;
        }
      }
      if (!hasSaved && valid) {
        this.commitSave();
      }
    },
    buildValuePairs() {
      return this.widgetValue.split("\n").map(line => {
        let parts = line
          .trim()
          .split("|")
          .filter(part => part.trim().length > 0);
        if (parts.length < 2) {
          parts.push(parts[0].trim().replace(/_+/g, " "));
        }
        return parts;
      });
    },
    saveEnabledKeys(valPairs) {
      const defaultKeys = this.defaultOptions.map(op => op.key);
      const mappedValue = valPairs.map(line =>
        line[0]
          .split("|")
          .shift()
          .trim()
      );
      if (this.defaultListId.length > 3) {
        this.value = mappedValue;
        const newValues = mappedValue.filter(k => defaultKeys.indexOf(k) < 0);
        saveSetting(this.defaultListId, {
          value: defaultKeys.concat(newValues)
        });
      } else {
        this.value = mappedValue.filter(k => defaultKeys.indexOf(k) >= 0);
      }
      this.commitSave();
    },
    commitSave() {
      const { _id } = this.setting;
      saveSetting(_id, {
        value: this.value,
        weight: parseInt(this.weight)
      }).then(data => {
        if (data.key) {
          this.saveMessage = "Setting has been saved successfully";
          let sts = this.settings;
          const { key } = data;
          const keys = Object.keys(sts);
          if (keys.indexOf(key) >= 0) {
            sts[key] = data.value;
            this.setSettings(sts);
            setTimeout(() => {
              this.$parent.close(data);
            }, 1000);
          }
        }
      });
    },
    saveRelatedLabels(valPairs) {
      if (this.snippetKey.length > 5) {
        const savedSnippets = valPairs.map(vp => {
          const itemKey = vp[0]
            .toLowerCase()
            .trim()
            .replace(/-/g, "_");
          return {
            key: this.snippetKey + "__" + itemKey,
            value: vp[1].trim(),
            format: "text"
          };
        });
        bulkSaveSnippets(savedSnippets).then(data => {
          if (data.items) {
            let index = -1;
            let sns = this.snippets;
            for (const sn of data.items) {
              index = this.snippets.findIndex(s => s.key === sn.key);
              if (index >= 0) {
                sns[index].value = data.value;
              } else {
                sns.push(sn);
              }
            }
            this.setSnippets(sns);
          }
        });
      }
    },
    mapOptions() {
      let setMatchedList = false;
      switch (this.setting.key) {
        case "enabled_art_media":
          this.defaultOptions = this.extractOptions("work", "medium");
          setMatchedList = true;
          break;
        case "enabled_graduate_statuses":
          this.defaultOptions = this.extractOptions(
            "submission",
            "graduateStatus"
          );
          break;
      }
      if (setMatchedList) {
        const matchedItem = this.$parent.items.find(
          item => item.key === "available_art_media"
        );
        if (matchedItem) {
          this.defaultListId = matchedItem._id;
        }
      }
      if (this.widgetValue.length > 0) {
        this.widgetValue = this.widgetValue
          .split("\n")
          .map(line => {
            let parts = [line];
            const snipKey = this.snippetKey + "__" + line.toLowerCase();
            const snip = this.snippets.find(
              sn => sn.key.toLowerCase() === snipKey
            );

            if (snip) {
              parts.push(snip.value);
            } else {
              parts.push(line.replace(/_+/g, " "));
            }
            return parts.join("|");
          })
          .join("\n");
      }
    },
    extractOptions(group, key) {
      return extractNestedOptions(group, key, this.options);
    },
    addOption() {
      if (this.setting.widget === "key_value_list") {
        if (this.value instanceof Array) {
          const newIndex = this.value.length;
          this.value.push({
            key: "newkey__" + newIndex,
            value: ""
          });
        }
      }
    },
    assignOptionKey(index, pKey) {
      if (this.value instanceof Array && index < this.value.length) {
        const targetOpt = this.value[index];
        if (targetOpt instanceof Object) {
          const { key } = targetOpt;
          if (key.length < 20 && this.isNew({ key: pKey })) {
            const txt = targetOpt.value.trim();
            this.value[index].key = toKeyString(txt);
          }
        }
      }
    },
    isNew(item) {
      if (!item.key) {
        item.key = "newkey__";
      }
      if (
        this.defaultOptions instanceof Array &&
        this.defaultOptions.length > 2
      ) {
        return (
          this.defaultOptions.indexOf(item.key) < 0 ||
          item.key.indexOf("newkey__") === 0 ||
          item.key.length < 3
        );
      }
      return false;
    },
    removeOption(index) {
      if (this.value instanceof Array) {
        if (index < this.value.length && index >= 0) {
          this.value.splice(index, 1);
        }
      }
    }
  }
};
</script>